import {
  buildingGeomAttributeTagNew,
  INDEX_ATTRIBUTE,
  type BuildingGeomTag,
} from "building-types/buildingGeomTag/buildingGeomTagTypes";
import type { Rail, RailDefault, RailingGeomGroup, RailingRailSegment } from "building-types/settings/railingTypes";
import { RailType } from "building-types/settings/railingTypes";
import type { ViewEntityDescriptor } from "building-types/views/viewEntityTypes";
import type { MaterialSrc } from "common/materials/materialSrc";
import { extrudeFaceAlongPolyLine3 } from "geom/extrude";
import { polyLine2New } from "geom/polyLine2Types";
import type { PolyLine3 } from "geom/polyLine3";
import {
  polyLine3Copy,
  polyLine3EdgeCount,
  polyLine3ExtendBothEnds,
  polyLine3ExtendEnd,
  polyLine3ExtendStart,
  polyLine3ExtractEdge,
  polyLine3Move,
} from "geom/polyLine3";
import { uvGenerationForRailingRail } from "../../utils/uvGenerationForEntities";
import type { Path3OnSurfaces } from "./surfaces";

interface MakeBottomRailOptions {
  readonly group: RailingGeomGroup;
  readonly railingPathEdge3s: Path3OnSurfaces[];
  readonly bottomRail: RailDefault;
  readonly bottomRailThicknessResolved: number;
  readonly bottomRailHeight: number;
  readonly materialSrc: MaterialSrc | undefined;
  readonly viewEntityDescriptor: ViewEntityDescriptor;
  readonly postHalfWidth: number;
}

export function makeBottomRail({
  group,
  railingPathEdge3s,
  bottomRail,
  bottomRailThicknessResolved,
  bottomRailHeight,
  materialSrc,
  viewEntityDescriptor,
  postHalfWidth,
}: MakeBottomRailOptions) {
  const halfThickness = bottomRailThicknessResolved / 2;
  const halfWidth = bottomRail.width / 2;
  const verticalOffset = bottomRailHeight - bottomRailThicknessResolved / 2;
  let segmentIndexOffset = 0;

  for (let i = 0; i < railingPathEdge3s.length; i++) {
    const { polyLine3 } = railingPathEdge3s[i];
    const firstSegmentIndex = segmentIndexOffset;
    segmentIndexOffset += polyLine3 ? polyLine3EdgeCount(polyLine3) : 1;
    if (!polyLine3) {
      continue;
    }
    const polyLine3Resolved = adjustPolyLine3StartAndEndExtensions(
      i,
      railingPathEdge3s,
      polyLine3,
      verticalOffset,
      halfWidth,
      postHalfWidth,
    );
    const segments = makeRailSegments(polyLine3Resolved, firstSegmentIndex);
    const railGeom = extrudeFaceAlongPolyLine3(
      [
        polyLine2New([
          -halfWidth,
          -halfThickness,
          -halfWidth,
          halfThickness,
          halfWidth,
          halfThickness,
          halfWidth,
          -halfThickness,
          -halfWidth,
          -halfThickness,
        ]),
      ],
      polyLine3Resolved,
      0,
      0,
      1,
      null,
      null,
      makeSegmentFaceTags(segments),
    );
    group.group.add(railGeom, {
      type: "RAIL",
      railType: "BOTTOM",
      materialSrc,
      width: bottomRail.width,
      thickness: bottomRailThicknessResolved,
      height: bottomRailHeight,
      segments,
      uvGen: uvGenerationForRailingRail(),
      viewEntityDescriptor,
    });
  }
}

interface MakeTopRailOptions {
  readonly group: RailingGeomGroup;
  readonly railingPathEdge3s: Path3OnSurfaces[];
  readonly topRail: RailDefault;
  readonly topRailThicknessResolved: number;
  readonly topRailHeight: number;
  readonly materialSrc: MaterialSrc | undefined;
  readonly viewEntityDescriptor: ViewEntityDescriptor;
  readonly postHalfWidth: number;
}

export function makeTopRail({
  group,
  railingPathEdge3s,
  topRail,
  topRailThicknessResolved,
  topRailHeight,
  materialSrc,
  viewEntityDescriptor,
  postHalfWidth,
}: MakeTopRailOptions) {
  const halfThickness = topRailThicknessResolved / 2;
  const halfWidth = topRail.width / 2;
  const verticalOffset = topRailHeight - topRailThicknessResolved / 2;
  let segmentIndexOffset = 0;

  for (let i = 0; i < railingPathEdge3s.length; i++) {
    const { polyLine3, gapPolyLine3s } = railingPathEdge3s[i];
    const firstSegmentIndex = segmentIndexOffset;
    segmentIndexOffset += polyLine3 ? polyLine3EdgeCount(polyLine3) : 1;
    if (!polyLine3) {
      continue;
    }
    const polyLine3Resolved = adjustPolyLine3StartAndEndExtensions(
      i,
      railingPathEdge3s,
      polyLine3,
      verticalOffset,
      halfWidth,
      postHalfWidth,
    );
    const segments = makeRailSegments(polyLine3Resolved, firstSegmentIndex);
    const railGeom = extrudeFaceAlongPolyLine3(
      [
        polyLine2New([
          -halfWidth,
          -halfThickness,
          -halfWidth,
          halfThickness,
          halfWidth,
          halfThickness,
          halfWidth,
          -halfThickness,
          -halfWidth,
          -halfThickness,
        ]),
      ],
      polyLine3Resolved,
      0,
      0,
      1,
      null,
      null,
      makeSegmentFaceTags(segments),
    );
    group.group.add(railGeom, {
      type: "RAIL",
      railType: "TOP",
      materialSrc,
      width: topRail.width,
      thickness: topRailThicknessResolved,
      height: topRailHeight,
      segments,
      uvGen: uvGenerationForRailingRail(),
      viewEntityDescriptor,
    });

    for (const gapPolyLine3 of gapPolyLine3s) {
      const resolvedGapPolyLine3 = polyLine3ExtendBothEnds(
        polyLine3Move(polyLine3Copy(gapPolyLine3), 0, 0, verticalOffset),
        halfWidth,
      );
      // A gap piece bridges a height change inside one authored edge, so it reports as part of that
      // edge's run rather than as a run of its own.
      const gapSegments = makeRailSegments(resolvedGapPolyLine3, firstSegmentIndex, false);
      const gapRailGeom = extrudeFaceAlongPolyLine3(
        [
          polyLine2New([
            -halfWidth,
            -halfThickness,
            -halfWidth,
            halfThickness,
            halfWidth,
            halfThickness,
            halfWidth,
            -halfThickness,
            -halfWidth,
            -halfThickness,
          ]),
        ],
        resolvedGapPolyLine3,
        0,
        0,
        1,
        null,
        null,
        makeSegmentFaceTags(gapSegments),
      );
      group.group.add(gapRailGeom, {
        type: "RAIL",
        railType: "TOP",
        materialSrc,
        width: topRail.width,
        thickness: topRailThicknessResolved,
        height: topRailHeight,
        segments: gapSegments,
        uvGen: uvGenerationForRailingRail(),
        viewEntityDescriptor,
      });
    }
  }
}

/**
 * Splits a resolved rail centerline into the runs it should be reported as: one run per centerline edge,
 * each carrying only its own edge so consumers can measure it without re-deriving the split. Pass
 * `splitPerEdge: false` to keep the whole centerline on `firstSegmentIndex` instead.
 */
function makeRailSegments(centerLine: PolyLine3, firstSegmentIndex: number, splitPerEdge = true): RailingRailSegment[] {
  const edgeCount = polyLine3EdgeCount(centerLine);
  if (edgeCount === 0) {
    // A degenerate centerline extrudes no faces, so there is no run to report.
    return [];
  }
  if (!splitPerEdge) {
    return [{ segmentIndex: firstSegmentIndex, centerLine, edgeCount }];
  }
  const segments: RailingRailSegment[] = [];
  for (let edgeIndex = 0; edgeIndex < edgeCount; edgeIndex++) {
    segments.push({
      segmentIndex: firstSegmentIndex + edgeIndex,
      centerLine: polyLine3ExtractEdge(centerLine, edgeIndex),
      edgeCount: 1,
    });
  }
  return segments;
}

/** Tags each face the extrusion produces with the index of the run it belongs to, one entry per edge. */
function makeSegmentFaceTags(segments: readonly RailingRailSegment[]): BuildingGeomTag[][] {
  const tags: BuildingGeomTag[][] = [];
  for (const { segmentIndex, edgeCount } of segments) {
    for (let i = 0; i < edgeCount; i++) {
      tags.push([buildingGeomAttributeTagNew(INDEX_ATTRIBUTE, segmentIndex, "NUMBER")]);
    }
  }
  return tags;
}

function adjustPolyLine3StartAndEndExtensions(
  index: number,
  railingPath3: Path3OnSurfaces[],
  polyLine3: PolyLine3,
  verticalOffset: number,
  halfWidth: number,
  postHalfWidth: number,
) {
  let p3Resolved: PolyLine3;
  if (index === 0 && index === railingPath3.length - 1) {
    p3Resolved = polyLine3ExtendBothEnds(polyLine3Move(polyLine3Copy(polyLine3), 0, 0, verticalOffset), postHalfWidth);
  } else if (index === 0) {
    p3Resolved = polyLine3ExtendStart(
      polyLine3ExtendEnd(polyLine3Move(polyLine3Copy(polyLine3), 0, 0, verticalOffset), halfWidth),
      postHalfWidth,
    );
  } else if (index === railingPath3.length - 1) {
    p3Resolved = polyLine3ExtendEnd(
      polyLine3ExtendStart(polyLine3Move(polyLine3Copy(polyLine3), 0, 0, verticalOffset), halfWidth),
      postHalfWidth,
    );
  } else {
    p3Resolved = polyLine3ExtendBothEnds(polyLine3Move(polyLine3Copy(polyLine3), 0, 0, verticalOffset), halfWidth);
  }
  return p3Resolved;
}

export function getRailThickness(rail: Rail | undefined): number {
  if (!rail) {
    return 0;
  }
  switch (rail.type) {
    case RailType.RAIL_DEFAULT:
      return rail.thickness ?? 0;
    default:
      return 0;
  }
}
